import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft, Play } from "lucide-react";
import levelsData from "../data/levels.json";
import topicsData from "../data/topics.json";
import gameConfig from "../data/gameConfig.json";
import "../styles/levelSelector.css";
import { API_BASE } from "../config";
import apiFetch from "../apiFetch";

const MAX_TIER_BELOW_16 = gameConfig.ageGroup.belowCutoffTier;

function getUserId() {
  const stored = localStorage.getItem("userId") || localStorage.getItem("user_id");
  if (stored) return parseInt(stored, 10);
  try {
    const userJson = localStorage.getItem("user");
    if (userJson) {
      const parsed = JSON.parse(userJson);
      return parsed?.id || parsed?.userId || null;
    }
  } catch {}
  return null;
}

export default function LevelSelector() {
  const navigate = useNavigate();
  const location = useLocation();
  const { link, title } = location.state || {};

  const ageGroup = localStorage.getItem("ageGroup") || "below_16";
  const availableTiers = Object.entries(levelsData.tiers).filter(
    ([key]) => ageGroup === "above_16" || parseInt(key, 10) <= MAX_TIER_BELOW_16
  );

  const [tier, setTier] = useState(() => {
    const saved = localStorage.getItem("difficulty_tier");
    if (saved) {
      const n = parseInt(saved, 10);
      const maxTier = ageGroup === "above_16" ? 21 : MAX_TIER_BELOW_16;
      return n >= 1 && n <= maxTier ? saved : "1";
    }
    return "1";
  });
  const [topic, setTopic] = useState(topicsData.topics[0].id);

  useEffect(() => {
    // Only fall back to XP-based tier when the user has no saved preference
    if (localStorage.getItem("difficulty_tier")) return;
    const userId = getUserId();
    if (!userId) return;
    apiFetch(`/api/resources/xp/${userId}`)
      .then((res) => res.json())
      .then((data) => {
        const userLevel = data.level;
        if (userLevel && levelsData.tiers[String(userLevel)]) {
          setTier(String(userLevel));
        }
      })
      .catch(() => {});
  }, []);

  const selectedTopicDesc = topicsData.topics.find((t) => t.id === topic)?.description || "";
  const selectedTierDesc = levelsData.tiers[tier]?.description || "";

  const handleStart = () => {
    const tierName = levelsData.tiers[tier]?.name || tier;
    navigate(link || "/games/comprehension", {
      state: { fromGameSelect: true, tier, tierName, topicCategory: topic },
    });
  };

  return (
    <div className="level-selector">
      <div className="ls-card">
        <button className="ls-back-btn" onClick={() => navigate("/games/comprehension")}>
          <ArrowLeft size={16} /> Back to Games
        </button>

        <div className="ls-header">
          <h1 className="ls-title">{title || "Game Setup"}</h1>
          <p className="ls-subtitle">Customise your session before starting</p>
        </div>

        <div className="ls-controls">
          <div className="ls-control-group">
            <label className="ls-label">Difficulty Tier</label>
            <select
              className="ls-select"
              value={tier}
              onChange={(e) => setTier(e.target.value)}
            >
              {availableTiers.map(([key, t]) => (
                <option key={key} value={key}>
                  {t.name}
                </option>
              ))}
            </select>
            {selectedTierDesc && <p className="ls-hint">{selectedTierDesc}</p>}
          </div>

          <div className="ls-control-group">
            <label className="ls-label">Topic</label>
            <select
              className="ls-select"
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
            >
              {topicsData.topics.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.label}
                </option>
              ))}
            </select>
            {selectedTopicDesc && <p className="ls-hint">{selectedTopicDesc}</p>}
          </div>
        </div>

        <button className="ls-start-btn" onClick={handleStart}>
          <Play size={18} /> Start Game
        </button>
      </div>
    </div>
  );
}
